<template>
    <!-- Outer wrapper for resize handle visibility -->
    <div v-if="portalContentAndDesignStore.portalInitialized"
         class="flex-none h-screen relative"
         :style="{ width: sidebarWidth + 'px' }"
         ref="sidebarRef"
    >
        <!-- Grip indicator at top, on right border (outside overflow container) -->
        <div
            class="absolute top-4 right-0 translate-x-1/2 bg-white border border-gray-200 rounded px-0.5 py-1.5 shadow-sm z-[99999] cursor-ew-resize"
            @mousedown="startResize"
        >
            <div class="flex gap-px">
                <div class="flex flex-col gap-0.5">
                    <div class="w-0.5 h-0.5 rounded-full bg-gray-400"></div>
                    <div class="w-0.5 h-0.5 rounded-full bg-gray-400"></div>
                    <div class="w-0.5 h-0.5 rounded-full bg-gray-400"></div>
                </div>
                <div class="flex flex-col gap-0.5">
                    <div class="w-0.5 h-0.5 rounded-full bg-gray-400"></div>
                    <div class="w-0.5 h-0.5 rounded-full bg-gray-400"></div>
                    <div class="w-0.5 h-0.5 rounded-full bg-gray-400"></div>
                </div>
            </div>
        </div>
        <!-- Resize edge handle -->
        <div
            class="absolute top-0 right-0 w-1.5 h-full cursor-ew-resize z-[99998] hover:bg-teal-500/50 transition-colors"
            :class="{ 'bg-teal-500/50': isResizing }"
            @mousedown="startResize"
        ></div>
        <!-- Sidebar container with absolute positioned header/footer -->
        <div class="absolute inset-0 bg-white border-r shadow-xl" style="font-family: Inter, sans-serif;">
            <!-- Top navigation (absolute top) -->
            <div class="absolute top-0 left-0 right-0 bg-white z-10">
                <div class="flex justify-between items-center bg-white px-3 py-4 border-b text-gray-900">
                    <btn :href="backToSettingsRoute" icon-name="arrow-left-circle" type="secondary" size="small" @click="resetDesignerState">Back to Settings</btn>
                    <btn :href="closeLiveDesignerRoute" icon-name="close" type="secondary" size="small" @click="resetDesignerState"></btn>
                </div>
                <div class="flex bg-gray-50 px-3 pt-3 border-b">
                    <tabs>
                      <tab
                          v-if="showThemesTab"
                          @click="setActiveLiveDesignerTab('themes')"
                          :active="activeLiveDesignerTab === 'themes'"
                          padding="py-3 px-5"
                          inactive-class="bg-gray-50 border-b"
                      >
                        <icon name="swatch"></icon>
                      </tab>
                      <tab
                          @click="setActiveLiveDesignerTab('layout')"
                          :active="activeLiveDesignerTab === 'layout'"
                          padding="py-3 px-5"
                          inactive-class="bg-gray-50 border-b"
                      >
                        <icon name="brush"></icon>
                      </tab>
                      <tab
                          v-if="showPageContentTab"
                          @click="setActiveLiveDesignerTab('page-content')"
                          :active="activeLiveDesignerTab === 'page-content'"
                          padding="py-3 px-5"
                          inactive-class="bg-gray-50 border-b"
                      >
                        <icon name="pencil-square"></icon>
                      </tab>
                      <tab
                          @click="setActiveLiveDesignerTab('translations')"
                          :active="activeLiveDesignerTab === 'translations'"
                          padding="py-3 px-5"
                          inactive-class="bg-gray-50 border-b"
                      >
                        <icon name="language"></icon>
                      </tab>
                    </tabs>
                </div>
            </div>
            <!-- /Top navigation -->

            <!-- Scrollable content area (fills space between header and footer) -->
            <div class="absolute top-[118px] bottom-[73px] left-0 right-0 overflow-y-auto" ref="scrollContainerRef">
                <div class="px-3 pt-6 pb-5 space-y-4 text-sm text-gray-500">
                <template v-if="activeLiveDesignerTab === 'translations'">
                    <p>Manage your portal translations here.</p>
                    <translation-prefill-select-confirm />
                </template>
                <template v-else-if="activeLiveDesignerTab === 'themes'">
                    <theme-library />
                </template>
                <template v-else>
                    <p>
                        <span v-if="shouldShowLiveDesignerPageSwitcher" class="page-switcher-container relative inline-block">
                            <button
                                @click="pageSwitcherOpen = !pageSwitcherOpen"
                                class="inline-flex items-center gap-0.5 font-medium text-gray-700 hover:text-gray-900 underline decoration-dashed underline-offset-2 decoration-gray-400"
                            >
                                <span>{{ currentPageLabel }}</span>
                                <icon :name="pageSwitcherOpen ? 'arrow-up-s' : 'arrow-down-s'" size="14" class="text-gray-400"></icon>
                            </button>
                            <div
                                v-if="pageSwitcherOpen"
                                class="absolute top-full left-0 mt-1 bg-white rounded shadow-lg border border-gray-200 py-1 min-w-[120px] z-50"
                            >
                                <a
                                    href="/program"
                                    class="block px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors"
                                    :class="isRoute('partners.program') ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-900'"
                                    @click="handleNavigation('/program', $event)"
                                >
                                    Program
                                </a>
                                <a
                                    href="/dashboard"
                                    class="block px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors"
                                    :class="isRoute('partners.dashboard') ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-900'"
                                    @click="handleDashboardNavigation($event)"
                                >
                                    Dashboard
                                </a>
                                <a
                                    href="/login"
                                    class="block px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors"
                                    :class="isRoute('partners.login.show') ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-900'"
                                    @click="handleNavigation('/login', $event)"
                                >
                                    Log-in
                                </a>
                                <a
                                    href="/register"
                                    class="block px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors"
                                    :class="isRoute('partners.register.show') ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-900'"
                                    @click="handleNavigation('/register', $event)"
                                >
                                    Sign-up
                                </a>
                                <a
                                    v-if="!isReferralPortal && leadsEnabled"
                                    href="/submit"
                                    class="block px-3 py-1.5 text-sm hover:bg-gray-50 transition-colors"
                                    :class="isRoute('partners.leads.show') ? 'text-gray-900 font-medium bg-gray-50' : 'text-gray-900'"
                                    @click="handleNavigation('/submit', $event)"
                                >
                                    Leads
                                </a>
                            </div>
                        </span><span v-else class="font-medium text-gray-700">{{ currentPageLabel }}</span>
                        page — customize design in real-time.
                    </p>
                </template>
            </div>
            <template v-if="activeLiveDesignerTab === 'layout'">
              <div id="accordion-collapse-layout" class="w-full text-gray-900">

                  <!-- For referral portals, always show the same settings regardless of page -->
                  <template v-if="isReferralPortal">
                      <live-designer-accordion title="Branding">
                          <template #content>
                              <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <brand-settings />
                              </div>
                          </template>
                      </live-designer-accordion>

                      <live-designer-accordion title="Default">
                          <template #content>
                              <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <referral-portal-default-settings />
                              </div>
                          </template>
                      </live-designer-accordion>

                      <live-designer-accordion title="Content">
                          <template #content>
                              <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <referral-portal-layout-settings />
                              </div>
                          </template>
                      </live-designer-accordion>
                  </template>

                  <!-- For affiliate portals -->
                  <template v-else>
                      <live-designer-accordion title="Branding">
                          <template #content>
                              <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <brand-settings :auth-view="authView" />
                              </div>
                          </template>
                      </live-designer-accordion>

                      <template v-if="showPublicSettings">
                          <live-designer-accordion title="Background">
                              <template #content>
                                  <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                      <public-default-settings />
                                  </div>
                              </template>
                          </live-designer-accordion>

                          <live-designer-accordion title="Form style">
                              <template #content>
                                  <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                      <public-content-settings />
                                  </div>
                              </template>
                          </live-designer-accordion>
                      </template>

                      <template v-else>
                          <live-designer-accordion title="Colors">
                              <template #content>
                                  <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                      <portal-default-settings :auth-view="authView" />
                                  </div>
                              </template>
                          </live-designer-accordion>
                          <live-designer-accordion title="Layout">
                              <template #content>
                                  <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                      <portal-layout-settings :auth-view="authView" />
                                  </div>
                              </template>
                          </live-designer-accordion>
                          <live-designer-accordion title="Navigation">
                              <template #content>
                                  <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                      <portal-navigation-settings :auth-view="authView" />
                                  </div>
                              </template>
                          </live-designer-accordion>
                      </template>
                  </template>

                  <!-- Page layout settings for all public pages (affiliate portals only) -->
                  <template v-if="!isReferralPortal && showPublicSettings">
                      <live-designer-accordion
                          v-if="!isRoute('partners.register.show') || !portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance"
                          title="Page layout"
                      >
                          <template #content>
                              <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <public-login-settings />
                              </div>
                          </template>
                      </live-designer-accordion>

                      <live-designer-accordion
                          v-if="isRoute('partners.register.show') && portalContentAndDesignStore.portalLayoutSettings.portal_public_pages_different_appearance"
                          title="Page layout"
                      >
                          <template #content>
                              <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                                  <public-signup-settings />
                              </div>
                          </template>
                      </live-designer-accordion>
                  </template>


              </div>
            </template>
            <template v-if="activeLiveDesignerTab === 'page-content'">
              <div id="accordion-collapse-page-content" class="w-full text-gray-900">
                  <live-designer-accordion
                      v-if="authView && !isReferralPortal"
                      title="Portal settings"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                          <portal-general-settings />
                      </div>
                    </template>
                  </live-designer-accordion>

                  <live-designer-accordion
                      v-if="isRoute('partners.program')"
                      title="Page details"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <program-page-details />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="isRoute('partners.program')"
                      title="Page content"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <base-resources-builder
                            name="landingPageResources"
                            :program-type="portalContentAndDesignStore.program.program_type.type"
                            :resources="portalContentAndDesignStore.landingPageResources.resources || []"
                            :file-upload-url="portalContentAndDesignStore.getFileResourcesUploadRoute"
                            :file-delete-url="portalContentAndDesignStore.getFileResourcesDeleteRoute"
                            zero-fields-title="Start building your page"
                            zero-fields-message="Start building your custom landing page by adding resources."
                            options-container-class=""
                            @update:value="portalContentAndDesignStore.landingPageResources.resources = $event"
                        />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <!-- Login Page Details - Separate for Referral vs Affiliate -->
                  <live-designer-accordion
                      v-if="isRoute('partners.login.show') && isReferralPortal"
                      title="Page details"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <referral-login-page-details />
                      </div>
                    </template>
                  </live-designer-accordion>

                  <!-- Signup Page Details - Separate for Referral vs Affiliate -->
                  <live-designer-accordion
                      v-if="isRoute('partners.register.show') && isReferralPortal"
                      title="Page details"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <referral-signup-page-details />
                      </div>
                    </template>
                  </live-designer-accordion>

                  <!-- Registration Form - Only for Referral Portal -->
                  <live-designer-accordion
                      v-if="isRoute('partners.register.show') && isReferralPortal"
                      title="Registration form"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <fields-builder
                            name="signup_fields"
                            v-model="portalContentAndDesignStore.signupPageSettings.signup_form.fields"
                            zero-fields-message="Add few fields for new customer signup form."
                            form-type="signup"
                            :program-id="portalContentAndDesignStore.program?.id"
                        />
                      </div>
                    </template>
                  </live-designer-accordion>

                  <!-- Additional Settings - Only for Referral Portal -->
                  <live-designer-accordion
                      v-if="isRoute('partners.register.show') && isReferralPortal"
                      title="Additional settings"
                  >
                      <template #content>
                          <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                              <signup-additional-settings />
                          </div>
                      </template>
                  </live-designer-accordion>

                  <!-- AFFILIATE PORTAL SPECIFIC SETTINGS -->
                  <!-- Login Page Settings - Only for Affiliate Portal (moved from Appearance tab) -->
                  <live-designer-accordion
                      v-if="isRoute('partners.login.show') && !isReferralPortal"
                      title="Page details"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <affiliate-login-page-settings />
                      </div>
                    </template>
                  </live-designer-accordion>

                  <!-- Signup Page Settings - Only for Affiliate Portal (includes branding) -->
                  <live-designer-accordion
                      v-if="isRoute('partners.register.show') && !isReferralPortal"
                      title="Page details"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <affiliate-signup-page-settings />
                      </div>
                    </template>
                  </live-designer-accordion>

                  <!-- Registration Form - Only for Affiliate Portal -->
                  <live-designer-accordion
                      v-if="isRoute('partners.register.show') && !isReferralPortal"
                      title="Registration form"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <fields-builder
                            name="signup_fields"
                            v-model="portalContentAndDesignStore.signupPageSettings.signup_form.fields"
                            zero-fields-message="Add few fields for new customer signup form."
                            form-type="signup"
                            :program-type="portalContentAndDesignStore.program?.program_type?.type || 'affiliate'"
                            :program-id="portalContentAndDesignStore.program?.id"
                        />
                      </div>
                    </template>
                  </live-designer-accordion>

                  <!-- Additional Settings - Only for Affiliate Portal -->
                  <live-designer-accordion
                      v-if="isRoute('partners.register.show') && !isReferralPortal"
                      title="Additional settings"
                  >
                      <template #content>
                          <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                              <signup-additional-settings />
                          </div>
                      </template>
                  </live-designer-accordion>

                  <!-- Dashboard Settings - Only for Referral Portal -->
                  <live-designer-accordion
                      v-if="isRoute('partners.dashboard') && isReferralPortal"
                      title="Page details"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <dashboard-page-details />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="isRoute('partners.dashboard') && isReferralPortal"
                      title="Dashboard settings"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <dashboard-page-settings />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="isRoute('partners.leads.show')"
                      title="Page details"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <leads-page-settings />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="isRoute('partners.leads.show')"
                      title="Custom fields"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <fields-builder
                            name="leads_fields"
                            v-model="portalContentAndDesignStore.leadsPageSettings.leads_form.fields"
                            zero-fields-message="Add few fields for new customer signup form."
                            form-type="signup"
                            :program-id="portalContentAndDesignStore.program?.id"
                        />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="authView && isOnApprovalPage"
                      title="Custom fields"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                        <fields-builder
                            name="approval_fields"
                            v-model="portalContentAndDesignStore.approvalPageSettings.approval_form.fields"
                            zero-fields-message="Add few fields for new customer approval."
                            form-type="approval"
                            :program-id="portalContentAndDesignStore.program?.id"
                        />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="authView && !isReferralPortal && isOnDashboardPage"
                      title="Dashboard content"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                          <dashboard-fields-resource-builder
                              v-model:resources="portalContentAndDesignStore.dashboardPageSettings.dashboard_resources"
                              :file_upload_url="portalContentAndDesignStore.getFileResourcesUploadRoute"
                              :file_delete_url="portalContentAndDesignStore.getFileResourcesDeleteRoute"
                              :available-partner-tags="availablePartnerTags"
                              zero-fields-title="Custom dashboard resources live here"
                              zero-fields-message="Here you can create your own dashboard resource page that will be visible to your partners.
                 You can share links, create text or content blocks and anything else your partners might need."
                          />
                      </div>
                    </template>
                  </live-designer-accordion>
                  <live-designer-accordion
                      v-if="authView && isOnResourcesPage"
                      title="Resources"
                  >
                    <template #content>
                      <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                          <resources-builder
                              :value="portalContentAndDesignStore.resourcesPageSettings.resources"
                              @update:value="portalContentAndDesignStore.resourcesPageSettings.resources = $event"
                              :file-upload-url="portalContentAndDesignStore.getResourcePageFileUploadRoute"
                              :file-delete-url="portalContentAndDesignStore.getResourcePageFileDeleteRoute"
                              :generate-example-url="portalContentAndDesignStore.getResourcePageGenerateExampleRoute"
                              :partner-tags="availablePartnerTags"
                              zero-fields-title="Resources live here"
                              zero-fields-message="Here you can create your own resource page that will be visible to your partners.
                 You can share links, create text or content blocks and anything else your partners might need."
                              :show-update-button="false"
                              is-live-designer-view
                          />
                      </div>
                    </template>
                  </live-designer-accordion>

              </div>
            </template>
            <template v-if="activeLiveDesignerTab === 'translations'">
                <live-designer-accordion v-for="(accordionData, index) in portalContentAndDesignStore.translationSettingsConfig" :key="`translations-${index}`"
                    :title="accordionData.label"
                >
                    <template #content>
                        <div class="p-3 border-t border-gray-200 grid grid-cols-1 gap-4">
                            <translation-settings :translations="accordionData.translations" />
                        </div>
                    </template>
                </live-designer-accordion>
            </template>
            </div>
            <!-- /Scrollable content area -->

            <!-- Footer (absolute bottom) -->
            <div class="absolute bottom-0 left-0 right-0 p-4 bg-gray-50 border-t">
                <div v-if="liveDesignerErrors.responseError || liveDesignerErrors.validationErrors" class="mb-4">
                    <form-errors :store-form-errors="liveDesignerErrors" />
                </div>
                <div class="flex items-center justify-between">
                    <btn v-if="hasUnsavedChanges" type="link" @click="resetLiveDesigner">
                        Revert changes
                    </btn>
                    <span v-else></span>
                    <btn
                        type="primary"
                        @click="saveLiveDesigner"
                        :loading="savingLiveDesignerSettings"
                        :disabled="savingLiveDesignerSettings"
                    >
                        Save
                    </btn>
                </div>
            </div>
            <!-- /Footer -->
        </div>
    </div>

    <!-- Unsaved changes confirmation modal -->
    <div v-if="showUnsavedChangesModal" class="fixed inset-0 z-[999999] flex items-center justify-center" style="font-family: Inter, sans-serif;">
        <div class="fixed inset-0 bg-black/40" @click="cancelNavigation"></div>
        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden ring-1 ring-black/5">
            <div class="p-8 pb-7">
                <h3 class="text-base font-semibold text-gray-900 mb-1">Unsaved changes</h3>
                <p class="text-sm text-gray-500 leading-relaxed">What would you like to do?</p>
            </div>
            <div class="px-8 pb-8">
                <btn
                    type="primary"
                    class="w-full"
                    @click="saveAndNavigate"
                    :loading="savingLiveDesignerSettings"
                    :disabled="savingLiveDesignerSettings"
                >
                    Save & continue
                </btn>
            </div>
            <div class="flex border-t border-gray-100">
                <button
                    @click="confirmNavigation"
                    class="flex-1 px-4 py-3 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                    Discard changes
                </button>
                <button
                    @click="cancelNavigation"
                    class="flex-1 px-4 py-3 text-sm font-medium text-gray-600 hover:bg-gray-50 hover:text-gray-900 border-l border-gray-100 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>

    <!-- Dashboard requires authentication modal -->
    <div v-if="showDashboardAuthModal" class="fixed inset-0 z-[999999] flex items-center justify-center" style="font-family: Inter, sans-serif;">
        <div class="fixed inset-0 bg-black/40" @click="showDashboardAuthModal = false"></div>
        <div class="relative bg-white rounded-xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden ring-1 ring-black/5">
            <div class="p-8 pb-7">
                <h3 class="text-base font-semibold text-gray-900 mb-1">Dashboard requires login</h3>
                <p class="text-sm text-gray-500 leading-relaxed">To preview the Dashboard page in the designer, you need to be logged in as a partner. Please sign up or log in first.</p>
            </div>
            <div class="px-8 pb-8 flex flex-col gap-2">
                <btn type="primary" class="w-full" :href="'/login'" @click="showDashboardAuthModal = false">
                    Log in as partner
                </btn>
                <btn type="light" class="w-full" :href="'/register'" @click="showDashboardAuthModal = false">
                    Sign up as partner
                </btn>
            </div>
            <div class="border-t border-gray-100">
                <button
                    @click="showDashboardAuthModal = false"
                    class="w-full px-4 py-3 text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 transition-colors"
                >
                    Cancel
                </button>
            </div>
        </div>
    </div>

    <!-- Success notification -->
    <notification
        :show="showSuccessNotification"
        title="Success"
        whitespace="whitespace-normal"
        @notificationClosed="showSuccessNotification = false"
    >
        <template #message>{{ liveDesignerSuccessMessage }}</template>
    </notification>
</template>

<script setup>
import {defineProps, reactive, ref, onMounted, onUnmounted, watch, computed, provide, nextTick} from 'vue';
import {useRoute} from 'vue-router';
import LiveDesignerAccordion from "./includes/Accordion.vue";
import BrandSettings from "./includes/BrandSettings.vue";
import {usePortalContentAndDesignStore} from "../../../stores/partners/portalContentAndDesignStore";
import PublicDefaultSettings from "./includes/PublicDefaultSettings.vue";
import PublicContentSettings from "./includes/PublicContentSettings.vue";
import PortalDefaultSettings from "./includes/PortalDefaultSettings.vue";
import PortalLayoutSettings from "./includes/PortalLayoutSettings.vue";
import PortalNavigationSettings from "./includes/PortalNavigationSettings.vue";
import ReferralPortalDefaultSettings from "../referral-portal/live-designer/includes/ReferralPortalDefaultSettings.vue";
import ReferralPortalLayoutSettings from "../referral-portal/live-designer/includes/ReferralPortalLayoutSettings.vue";
import PublicLoginSettings from "./includes/PublicLoginSettings.vue";
import PublicSignupSettings from "./includes/PublicSignupSettings.vue";
import Tabs from "../../../elements/tabs/Tabs.vue";
import Tab from "../../../elements/tabs/Tab.vue";

import FieldsBuilder from "../../app/fields-builder/FieldsBuilder.vue";
import Alert from "../../../elements/Alert.vue";
import FormErrors from "../Pub/Pages/Errors/FormErrors.vue";
import Btn from "../../../elements/Btn.vue";
import Notification from "../../../elements/Notification.vue";
// Conditional Vue Router import - only use if available
import DashboardFieldsResourceBuilder from "../../app/preferences/pages/dashboard/DashboardFieldsResourceBuilder.vue";
import ResourcesBuilder from "../../app/resources/ResourcesBuilder.vue";
import TranslationSettings from "./includes/TranslationSettings.vue";
// LandingPageSettings no longer needed - settings sync through landingPageResources
import ReferralSignupPageDetails from "./includes/ReferralSignupPageDetails.vue";
import AffiliateSignupPageSettings from "./includes/AffiliateSignupPageSettings.vue";
import SignupAdditionalSettings from "./includes/SignupAdditionalSettings.vue";
import ReferralLoginPageDetails from "./includes/ReferralLoginPageDetails.vue";
import DashboardPageDetails from "../referral-portal/live-designer/includes/DashboardPageDetails.vue";
import DashboardPageSettings from "../referral-portal/live-designer/includes/DashboardPageSettings.vue";
import ProgramPageDetails from "./includes/ProgramPageDetails.vue";
import LeadsPageSettings from "./includes/LeadsPageSettings.vue";
import TranslationPrefillSelectConfirm from "./includes/TranslationPrefillSelectConfirm.vue";
import AffiliateLoginPageSettings from "./includes/AffiliateLoginPageSettings.vue";
import PortalGeneralSettings from "./includes/PortalGeneralSettings.vue";
import ThemeLibrary from "./includes/ThemeLibrary.vue";

const props = defineProps({
    authView: {
        type: Boolean,
        default: false
    }
});

// Accordion group controller - only one accordion can be open at a time
const activeAccordion = ref(sessionStorage.getItem('live_designer_accordion') || null);
const accordionGroup = {
    activeAccordion,
    setActive: (title) => {
        activeAccordion.value = title;
        if (title) {
            sessionStorage.setItem('live_designer_accordion', title);
        } else {
            sessionStorage.removeItem('live_designer_accordion');
        }
    }
};
provide('accordionGroup', accordionGroup);

// Safe Vue Router usage - only for auth views that have Vue Router
// Don't call useRoute() here as router context may not be available
// Instead, we'll use path-based checks which are more reliable
// Return an empty object to avoid null reference errors in templates
const vueRouterRoute = computed(() => {
    return {};
});

// Reactive path checking for authenticated pages
// Initialize with current path if available (for SSR compatibility)
const currentPath = ref(typeof window !== 'undefined' && window.location ? window.location.pathname : '');

// Try to use Vue Router if available (for authenticated pages)
let vueRoute = null;
try {
    if (props.authView) {
        vueRoute = useRoute();
    }
} catch (e) {
    // Vue Router not available, will use window.location.pathname
    console.debug('Vue Router not available in LiveDesigner context');
}

// Update path when component mounts and on navigation
onMounted(() => {
    const updatePath = () => {
        if (typeof window !== 'undefined' && window.location) {
            currentPath.value = window.location.pathname;
        }
    };

    // Update immediately on mount
    updatePath();

    // If Vue Router is available, watch route changes
    if (vueRoute) {
        watch(() => vueRoute.path, (newPath) => {
            if (newPath) {
                currentPath.value = newPath;
            }
        }, { immediate: true });
    } else {
        // Fallback: Listen to popstate for browser navigation
        if (typeof window !== 'undefined') {
            window.addEventListener('popstate', updatePath);
            window.addEventListener('hashchange', updatePath);
        }
    }

    // Close page switcher dropdown when clicking outside
    document.addEventListener('click', closePageSwitcher);

    // Warn about unsaved changes when leaving the page (browser refresh/close)
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Intercept link clicks in the portal to show our custom modal
    document.addEventListener('click', handleLinkClick, true);

    // Set default accordion after all accordions have mounted
    nextTick(() => {
        if (!activeAccordion.value) {
            activeAccordion.value = 'Branding';
        }
        // Fall back to the Layout tab if a stale "themes" tab was persisted but
        // the themes tab is not available (e.g. referral portals).
        if (activeLiveDesignerTab.value === 'themes' && !showThemesTab.value) {
            setActiveLiveDesignerTab('layout');
        }
    });
});

// Handle browser beforeunload event (for refresh/close)
const handleBeforeUnload = (e) => {
    if (hasUnsavedChanges.value) {
        e.preventDefault();
        e.returnValue = '';
        return '';
    }
};

// Handle link clicks to show custom modal instead of navigating
const handleLinkClick = (e) => {
    // Only intercept if there are unsaved changes
    if (!hasUnsavedChanges.value) return;

    // Find the closest anchor tag
    const link = e.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');

    // Skip if it's a javascript: link, anchor, or external link
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) return;

    // Skip if it's within the page switcher (already handled)
    if (link.closest('.page-switcher-container')) return;

    // Skip if it's the Back to Settings or Close button (let them work normally with warning)
    if (link.closest('.absolute.top-0')) return;

    // Intercept the click and show our modal
    e.preventDefault();
    e.stopPropagation();
    pendingNavigationUrl.value = href;
    showUnsavedChangesModal.value = true;
};

// Cleanup resize listeners on unmount
onUnmounted(() => {
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', stopResize);
    document.removeEventListener('click', closePageSwitcher);
    window.removeEventListener('beforeunload', handleBeforeUnload);
    document.removeEventListener('click', handleLinkClick, true);
});

// Computed properties for route checking
const isOnDashboardPage = computed(() => {
    if (!props.authView) return false;
    const path = currentPath.value.replace(/\/$/, ''); // Remove trailing slash
    return path === '/dashboard';
});

const isOnResourcesPage = computed(() => {
    if (!props.authView) return false;
    const path = currentPath.value.replace(/\/$/, ''); // Remove trailing slash
    return path === '/resources';
});

const isOnApprovalPage = computed(() => {
    if (!props.authView) return false;
    const path = currentPath.value.replace(/\/$/, ''); // Remove trailing slash
    return path === '/approval';
});

const actionType = ref('');

const portalContentAndDesignStore =  usePortalContentAndDesignStore()

// Watch for changes to mark the store as dirty (skip first trigger on mount and during reset)
const hasInitialWatchTriggered = ref(false);
const isResetting = ref(false);
watch(
    () => [
        portalContentAndDesignStore.portalLayoutSettings,
        portalContentAndDesignStore.translationSettings,
        portalContentAndDesignStore.srcRoutes,
    ],
    () => {
        // Skip the first trigger which happens on component mount
        if (!hasInitialWatchTriggered.value) {
            hasInitialWatchTriggered.value = true;
            return;
        }
        // Skip if we're resetting to initial state
        if (isResetting.value) {
            return;
        }
        // Mark as dirty when user makes changes
        if (portalContentAndDesignStore.portalInitialized) {
            portalContentAndDesignStore.markAsDirty();
        }
    },
    { deep: true }
);

// Available partner tags for visibility filtering in resource builders
const availablePartnerTags = computed(() => portalContentAndDesignStore.availablePartnerTags || []);

const liveDesignerErrors = reactive({
            responseError: null,
            validationErrors: null
        });

const liveDesignerSuccessMessage = ref(null);
const showSuccessNotification = ref(false);
const savingLiveDesignerSettings = ref(false);

// Sidebar resize functionality
const sidebarRef = ref(null);
const scrollContainerRef = ref(null);
const MIN_SIDEBAR_WIDTH = 400;
const getDefaultSidebarWidth = () => {
    const vw = window.innerWidth;
    if (vw >= 1920) return 480;
    if (vw >= 1440) return 420;
    return 380;
};
const sidebarWidth = ref(parseInt(localStorage.getItem('liveDesignerSidebarWidth')) || getDefaultSidebarWidth());
const isResizing = ref(false);

const startResize = (e) => {
    isResizing.value = true;
    document.addEventListener('mousemove', handleResize);
    document.addEventListener('mouseup', stopResize);
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';
};

const handleResize = (e) => {
    if (!isResizing.value) return;

    const maxWidth = window.innerWidth / 2;
    let newWidth = e.clientX;

    // Clamp the width between min and max
    newWidth = Math.max(MIN_SIDEBAR_WIDTH, Math.min(newWidth, maxWidth));

    sidebarWidth.value = newWidth;
};

const stopResize = () => {
    isResizing.value = false;
    document.removeEventListener('mousemove', handleResize);
    document.removeEventListener('mouseup', stopResize);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';

    // Save to localStorage
    localStorage.setItem('liveDesignerSidebarWidth', sidebarWidth.value);
};

// Get current route name - works for both affiliate and referral portals
const getCurrentRouteName = () => {
    if (isReferralPortal.value) {
        // For referral portals, determine route from URL path
        const path = typeof window !== 'undefined' ? window.location.pathname : '';
        if (path.includes('/login')) return 'partners.login.show';
        if (path.includes('/register')) return 'partners.register.show';
        if (path.includes('/submit')) return 'partners.leads.show';
        if (path.includes('/program')) return 'partners.program';
        if (path.includes('/dashboard')) return 'partners.dashboard';
        return 'partners.program'; // Default for referral portals
    } else {
        // For affiliate portals, use Laravel route helper
        return route().current();
    }
};

const shouldShowLiveDesignerPageSwitcher = computed(() => {
    const currentRoute = getCurrentRouteName();
    return [
        "partners.program",
        "partners.login.show",
        "partners.register.show",
        "partners.password.request.show",
        "partners.leads.show",
        "partners.dashboard",
    ].includes(currentRoute);
});

const isActive = (routeName) => {
    const currentRoute = getCurrentRouteName();
    return currentRoute === routeName ? 'font-semibold' : '';
};

// Local isRoute function that works for both affiliate and referral portals
const isRoute = (routeName) => {
    return getCurrentRouteName() === routeName;
};

const saveLiveDesigner = async () => {
    actionType.value = 'save';
    await sendRequest();
};

const resetLiveDesigner = () => {
    isResetting.value = true;
    portalContentAndDesignStore.resetCurrentPageToInitialState();
    // Reset the flag after a tick to allow the watcher to skip
    setTimeout(() => {
        isResetting.value = false;
    }, 0);
};

const routeName = computed(() => getCurrentRouteName());

const showPublicSettings = computed(() => {
    // These are PUBLIC (non-authenticated) pages that use public_page_content_* settings
    // Note: partners.dashboard is the AUTHENTICATED dashboard, not a public page
    return [
        "partners.login.show",
        "partners.register.show",
        "partners.password.request.show",
        "partners.program",
        "partners.leads.show",
    ].includes(routeName.value);
});

const isReferralPortal = computed(() => {
    const programType = portalContentAndDesignStore.program?.program_type?.type ||
                       portalContentAndDesignStore.program?.programType?.type;

    return programType === 'refer-a-friend' || programType === 'refer_a_friend';
});

const leadsEnabled = computed(() => {
    return portalContentAndDesignStore.portalLayoutSettings?.program_leads_form_enabled ?? false;
});

const showThemesTab = computed(() => {
    // Predefined design themes are currently offered for affiliate portals only,
    // and are only populated server-side while the Live Designer is open.
    return !isReferralPortal.value && (portalContentAndDesignStore.designTemplates?.length || 0) > 0;
});

const showPageContentTab = computed(() => {
    // For referral portals, always use the isRoute check since they don't use Vue Router
    if (isReferralPortal.value) {
        return isRoute('partners.program')
         || isRoute('partners.login.show')
         || isRoute('partners.register.show')
         || isRoute('partners.leads.show')
         || isRoute('partners.dashboard')
    }

    // For affiliate portals - always show Content tab on authenticated pages
    if (props.authView || portalContentAndDesignStore.isAuthView) {
        return true;
    }
    else {
        return isRoute('partners.program')
         || isRoute('partners.login.show')
         || isRoute('partners.register.show')
         || isRoute('partners.leads.show')
         || isRoute('partners.dashboard')
    }

});

const sendRequest = async () => {

    savingLiveDesignerSettings.value = true;

    resetLiveDesignerErrorsAndMessages()



    let payload = {
        programId: portalContentAndDesignStore.program.id,
        actionType: actionType.value,
        ...portalContentAndDesignStore.translationSettings,
        ...portalContentAndDesignStore.portalLayoutSettings,
    }

    if (portalContentAndDesignStore.isAuthView){
        if (vueRouterRoute.value?.name === 'dashboard' || vueRouterRoute.value?.name === 'referral-dashboard' || vueRouterRoute.value?.path === '/dashboard' || currentPath.value === '/dashboard' || currentPath.value === '/dashboard/') {
            // Use the same approach as program page - construct form field mappings
            payload = {
                ...payload,
                // Map dashboard settings to form field names (like program page does)
                refer_a_friend_portal_page_title: portalContentAndDesignStore.dashboardPageSettings.refer_a_friend_portal_page_title || '',
                refer_a_friend_portal_page_description: portalContentAndDesignStore.dashboardPageSettings.refer_a_friend_portal_page_description || '',
                referral_portal_dashboard_branding: portalContentAndDesignStore.dashboardPageSettings.refer_a_friend_portal_dashboard_branding || 'symbol',
                dashboard_page_resources: JSON.stringify(portalContentAndDesignStore.dashboardPageSettings.dashboard_resources || []),
                // Include all dashboard toggle settings
                refer_a_friend_portal_show_stats: portalContentAndDesignStore.dashboardPageSettings.show_stats || false,
                refer_a_friend_portal_show_sharing_via_link: portalContentAndDesignStore.dashboardPageSettings.show_sharing_via_link || false,
                refer_a_friend_portal_show_sharing_via_email: portalContentAndDesignStore.dashboardPageSettings.show_sharing_via_email || false,
                refer_a_friend_portal_show_reward_balance: portalContentAndDesignStore.dashboardPageSettings.show_reward_balance || false,
                refer_a_friend_portal_show_unlocked_rewards: portalContentAndDesignStore.dashboardPageSettings.show_unlocked_rewards || false,
                refer_a_friend_portal_show_rewards_history: portalContentAndDesignStore.dashboardPageSettings.show_rewards_history || false,
            }

        }

        // Only persist resources once they have actually been loaded from the backend.
        // Otherwise (e.g. the resources page never mounted) we'd serialize an empty
        // array and overwrite/delete the program's stored resources.
        if ((vueRouterRoute.value?.name === 'resources' || currentPath.value === '/resources' || currentPath.value === '/resources/')
            && portalContentAndDesignStore.resourcesPageSettingsLoaded) {
            payload = {
                ...payload,
                resource_page_resources: JSON.stringify(portalContentAndDesignStore.resourcesPageSettings.resources || []),
            }
        }

        if (vueRouterRoute.value?.name === 'approval' || vueRouterRoute.value?.name === 'referral-approval' || currentPath.value === '/approval' || currentPath.value === '/approval/') {
            payload = {
                ...payload,
                approval_fields: JSON.stringify(portalContentAndDesignStore.approvalPageSettings.approval_form?.fields || []),
                approval_fields_title: portalContentAndDesignStore.approvalPageSettings.approval?.title,
                approval_fields_description: portalContentAndDesignStore.approvalPageSettings.approval?.description,
            }
        }
    }


    if (isRoute('partners.login.show')) {
        payload = {
            ...payload,
            ...portalContentAndDesignStore.translationSettings,
            program_login_page_branding_type: portalContentAndDesignStore.loginPageSettings?.program_login_page_branding_type || 'none',
        }
    }

    if (isRoute('partners.program')) {
        payload = {
            ...payload,
            ...portalContentAndDesignStore.landingPageSettings.translations,
            landing_page_resources: JSON.stringify(portalContentAndDesignStore.landingPageResources || {}),
        }
    }

    if (isRoute('partners.register.show')) {
        payload = {
            ...payload,
            ...portalContentAndDesignStore.translationSettings,
            portal_signup_tos_text: portalContentAndDesignStore.signupPageSettings.signup?.portal_signup_tos_text || '',
            portal_signup_tos_url: portalContentAndDesignStore.signupPageSettings.signup?.portal_signup_tos_url || '',
            portal_signup_tos_saved_locally: portalContentAndDesignStore.signupPageSettings.signup?.portal_signup_tos_saved_locally || false,
            portal_signup_marketing_consent_text: portalContentAndDesignStore.signupPageSettings.signup?.marketing_consent || '',
            program_signup_page_branding_type: portalContentAndDesignStore.signupPageSettings.signup?.program_signup_page_branding_type || 'none',
            signup_fields: JSON.stringify(portalContentAndDesignStore.signupPageSettings.signup_form.fields || []),
        }
    }

    if (isRoute('partners.leads.show')) {

        const program_leads_additional_section = portalContentAndDesignStore.leadsPageSettings.program_leads_include_additional_section || false;

        payload = {
            ...payload,
            ...portalContentAndDesignStore.translationSettings,
            leads_form_fields: JSON.stringify(portalContentAndDesignStore.leadsPageSettings.leads_form.fields || []),
            program_leads_page_branding_type: portalContentAndDesignStore.leadsPageSettings.program_leads_page_branding_type || 'none',
            program_leads_include_signup_links: portalContentAndDesignStore.leadsPageSettings.program_leads_include_signup_links || false,
            program_leads_include_additional_section: program_leads_additional_section,
        }

        if (program_leads_additional_section) {
            payload = {
                ...payload,
                program_leads_page_additional_description: portalContentAndDesignStore.leadsPageSettings.program_leads_page_additional_description || '',
            }
        }
    }

    // Dashboard settings are now handled in the main dashboard-specific section above

    if (isRoute('partners.password.request.show') || isRoute('partners.password.reset.show')) {
        payload = {
            ...payload,
            ...portalContentAndDesignStore.translationSettings,
        }
    }


    return axios.post(route('partners_int.live-designer.update'), payload)
        .then(response => {
            if (+response.data.status === 1) {
                resetLiveDesignerErrorsAndMessages()
                liveDesignerSuccessMessage.value = response.data.message;
                showSuccessNotification.value = true;

                // Mark as clean after successful save
                portalContentAndDesignStore.markAsClean();

                // Auto-hide notification after 3 seconds
                setTimeout(() => {
                    showSuccessNotification.value = false;
                }, 3000);

                // Reload page data after successful save to reflect changes
                if (vueRouterRoute.value?.name === 'dashboard' || vueRouterRoute.value?.name === 'referral-dashboard' || vueRouterRoute.value?.path === '/dashboard' || currentPath.value === '/dashboard' || currentPath.value === '/dashboard/') {
                    portalContentAndDesignStore.initializePortalPageContent('dashboard');
                }

                return true; // Indicate success
            } else {
                liveDesignerErrors.responseError = response.data.error;
                return false;
            }
        })
        .catch(error => {
            if (error.response && error.response.status === 422) {
                liveDesignerErrors.validationErrors = error.response.data.errors;
            } else {
                liveDesignerErrors.responseError = error.response?.data?.error || 'An error occurred. See console for details';
            }
            return false;
        }).finally(() => {
            savingLiveDesignerSettings.value = false;
        });
}

const resetLiveDesignerErrorsAndMessages = () => {
    liveDesignerErrors.responseError = null;
    liveDesignerErrors.validationErrors = null;
    liveDesignerSuccessMessage.value = null;
    showSuccessNotification.value = false;
}

const closeLiveDesignerRoute = route('partners.live-designer.close');

const backToSettingsRoute = computed(() => {
    if (portalContentAndDesignStore.program?.id) {
        return route('programs.portal.settings', { program: portalContentAndDesignStore.program.id });
    }
    return closeLiveDesignerRoute;
});

const resetDesignerState = () => {
    document.cookie = 'designer_state=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Domain=' + location.hostname;
    sessionStorage.removeItem('live_designer_tab');
    sessionStorage.removeItem('live_designer_accordion');
};

const activeLiveDesignerTab = ref(sessionStorage.getItem('live_designer_tab') || 'layout');

const setActiveLiveDesignerTab = (tab) => {
  activeLiveDesignerTab.value = tab;
  sessionStorage.setItem('live_designer_tab', tab);
}

// Page switcher dropdown state
const pageSwitcherOpen = ref(false);

// Unsaved changes modal state
const showUnsavedChangesModal = ref(false);
const pendingNavigationUrl = ref(null);

// Dashboard auth modal state
const showDashboardAuthModal = ref(false);

const currentPageLabel = computed(() => {
    if (isRoute('partners.program')) return 'Program';
    if (isRoute('partners.dashboard')) return 'Dashboard';
    if (isRoute('partners.login.show')) return 'Log-in';
    if (isRoute('partners.register.show')) return 'Sign-up';
    if (isRoute('partners.leads.show')) return 'Leads';
    return 'Pages';
});

// Check if there are unsaved changes
const hasUnsavedChanges = computed(() => {
    return portalContentAndDesignStore.hasUnsavedChanges;
});

// Handle navigation with unsaved changes check
const handleNavigation = (url, event) => {
    if (hasUnsavedChanges.value) {
        event.preventDefault();
        pendingNavigationUrl.value = url;
        showUnsavedChangesModal.value = true;
        pageSwitcherOpen.value = false;
    }
    // If no unsaved changes, let the default navigation happen
};

// Handle Dashboard navigation - requires authentication for non-auth views
const handleDashboardNavigation = (event) => {
    const isAuthenticated = props.authView || portalContentAndDesignStore.isAuthView;

    if (!isAuthenticated) {
        event.preventDefault();
        pageSwitcherOpen.value = false;
        showDashboardAuthModal.value = true;
        return;
    }

    handleNavigation('/dashboard', event);
};

// Confirm navigation (discard changes)
const confirmNavigation = () => {
    // Mark as clean to prevent beforeunload warning
    portalContentAndDesignStore.markAsClean();
    showUnsavedChangesModal.value = false;
    if (pendingNavigationUrl.value) {
        window.location.href = pendingNavigationUrl.value;
    }
};

// Cancel navigation
const cancelNavigation = () => {
    showUnsavedChangesModal.value = false;
    pendingNavigationUrl.value = null;
};

// Save changes and then navigate
const saveAndNavigate = async () => {
    // Trigger save
    actionType.value = 'save';
    const success = await sendRequest();

    // If save was successful, navigate
    if (success) {
        showUnsavedChangesModal.value = false;
        if (pendingNavigationUrl.value) {
            window.location.href = pendingNavigationUrl.value;
        }
    }
};

// Close dropdown when clicking outside
const closePageSwitcher = (e) => {
    if (!e.target.closest('.page-switcher-container')) {
        pageSwitcherOpen.value = false;
    }
};
</script>
